<template>
  <v-card class="tarjeta-mesas">
    <v-card-title class="titulo-modal">
      <span>
        <v-icon
          class="mr-1"
          size="20"
        >mdi-table-furniture</v-icon>
        Mesas
      </span>
      <v-btn
        icon
        variant="text"
        size="small"
        @click="$emit('onClose')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <p class="texto-ayuda">
        Registra las mesas de tu negocio para poder asignarlas a las reservas.
        Una mesa desactivada no aparece al crear reservas nuevas, pero conserva su historial.
      </p>

      <!-- Formulario de alta / edicion -->
      <div class="formulario">
        <div class="grid-formulario">
          <v-text-field
            v-model="mesaEnEdicion.name"
            class="fg-blue"
            label="Nombre"
            placeholder="Mesa 1"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="guardarMesa"
          />
          <v-text-field
            v-model.number="mesaEnEdicion.capacity"
            type="number"
            min="1"
            class="fg-blue"
            label="Capacidad"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="guardarMesa"
          />
          <v-text-field
            v-model="mesaEnEdicion.zone"
            class="fg-blue"
            label="Zona (opcional)"
            placeholder="Terraza"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="guardarMesa"
          />
        </div>

        <div class="acciones-formulario">
          <v-btn
            v-if="mesaEnEdicion.idTable"
            variant="text"
            size="small"
            @click="limpiarFormulario"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            size="small"
            :loading="guardando"
            @click="guardarMesa"
          >
            <v-icon start>
              {{ mesaEnEdicion.idTable ? 'mdi-content-save' : 'mdi-plus' }}
            </v-icon>
            {{ mesaEnEdicion.idTable ? 'Guardar cambios' : 'Agregar mesa' }}
          </v-btn>
        </div>
      </div>

      <v-alert
        v-if="error"
        type="error"
        density="compact"
        class="mb-3"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <!-- Listado -->
      <div
        v-if="cargando"
        class="estado-vacio"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="28"
        />
      </div>

      <div
        v-else-if="mesas.length === 0"
        class="estado-vacio"
      >
        Todavia no tienes mesas registradas
      </div>

      <div
        v-else
        class="lista-mesas"
      >
        <div
          v-for="mesa in mesas"
          :key="mesa.idTable"
          class="fila-mesa"
          :class="{ 'mesa-inactiva': !mesa.active }"
        >
          <div class="datos-mesa">
            <div class="nombre-mesa">
              {{ mesa.name }}
              <v-chip
                v-if="!mesa.active"
                size="x-small"
                color="grey"
                variant="tonal"
              >
                Inactiva
              </v-chip>
            </div>
            <div class="detalle-mesa">
              <v-icon size="13">
                mdi-account-group
              </v-icon>
              {{ mesa.capacity }}
              <template v-if="mesa.zone">
                &middot; {{ mesa.zone }}
              </template>
            </div>
          </div>

          <div class="botones-mesa">
            <v-btn
              icon
              variant="text"
              size="small"
              :title="mesa.active ? 'Desactivar' : 'Activar'"
              @click="alternarActiva(mesa)"
            >
              <v-icon size="19">
                {{ mesa.active ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              title="Editar"
              @click="editarMesa(mesa)"
            >
              <v-icon size="19">
                mdi-pencil
              </v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              color="red"
              title="Eliminar"
              @click="eliminarMesa(mesa)"
            >
              <v-icon size="19">
                mdi-delete
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import apisReservas from '@/apis/apisReservas'

function mesaVacia() {
  return {
    idTable: null,
    idCompany: null,
    name: '',
    capacity: 4,
    zone: '',
    active: true
  }
}

export default {
  name: 'DialogMesas',
  props: {
    idEmpresa: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['onClose', 'alCambiarMesas'],
  data() {
    return {
      mesas: [],
      mesaEnEdicion: mesaVacia(),
      cargando: false,
      guardando: false,
      error: ''
    }
  },
  created() {
    this.cargarMesas()
  },
  methods: {
    cargarMesas() {
      this.cargando = true

      apisReservas.listarMesas(this.idEmpresa)
        .then(mesas => {
          this.mesas = mesas
        })
        .catch(error => {
          this.error = error.message
        })
        .finally(() => {
          this.cargando = false
        })
    },
    guardarMesa() {
      this.error = ''

      if (!this.mesaEnEdicion.name || this.mesaEnEdicion.name.trim() === '') {
        this.error = 'La mesa necesita un nombre'
        return
      }
      if (!this.mesaEnEdicion.capacity || this.mesaEnEdicion.capacity < 1) {
        this.error = 'La capacidad debe ser al menos 1'
        return
      }

      this.guardando = true

      const mesa = Object.assign({}, this.mesaEnEdicion, {
        idCompany: Number(this.idEmpresa)
      })

      apisReservas.guardarMesa(mesa)
        .then(() => {
          this.limpiarFormulario()
          this.cargarMesas()
          this.$emit('alCambiarMesas')
        })
        .catch(error => {
          this.error = error.message
        })
        .finally(() => {
          this.guardando = false
        })
    },
    editarMesa(mesa) {
      this.mesaEnEdicion = Object.assign({}, mesa)
      this.error = ''
    },
    alternarActiva(mesa) {
      const actualizada = Object.assign({}, mesa, { active: !mesa.active })

      apisReservas.guardarMesa(actualizada)
        .then(() => {
          this.cargarMesas()
          this.$emit('alCambiarMesas')
        })
        .catch(error => {
          this.error = error.message
        })
    },
    eliminarMesa(mesa) {
      this.error = ''

      // El backend responde 409 si la mesa ya tiene reservas asociadas y ese
      // mensaje es el que se le muestra al usuario.
      apisReservas.eliminarMesa(mesa.idTable)
        .then(() => {
          if (this.mesaEnEdicion.idTable === mesa.idTable) {
            this.limpiarFormulario()
          }
          this.cargarMesas()
          this.$emit('alCambiarMesas')
        })
        .catch(error => {
          this.error = error.message
        })
    },
    limpiarFormulario() {
      this.mesaEnEdicion = mesaVacia()
    }
  }
}
</script>

<style scoped>
.tarjeta-mesas {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}
.titulo-modal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1rem;
  font-weight: 700;
}
.texto-ayuda {
  font-size: 0.8rem;
  color: #777;
  margin: 0 0 12px;
}
.formulario {
  background: #f7f9fc;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 14px;
}
.grid-formulario {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
.acciones-formulario {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}
.lista-mesas {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fila-mesa {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  padding: 8px 10px;
}
.mesa-inactiva {
  opacity: 0.55;
}
.nombre-mesa {
  font-weight: 600;
  font-size: 0.92rem;
  display: flex;
  align-items: center;
  gap: 6px;
}
.detalle-mesa {
  font-size: 0.78rem;
  color: #777;
  display: flex;
  align-items: center;
  gap: 3px;
}
.botones-mesa {
  display: flex;
  flex-shrink: 0;
}
.estado-vacio {
  text-align: center;
  color: #999;
  font-size: 0.85rem;
  padding: 22px 0;
}

@media screen and (min-width: 600px) {
  .grid-formulario {
    grid-template-columns: 2fr 1fr 2fr;
  }
}
</style>
